/* eslint-disable react/prop-types */
import { useMotionValue, useSpring, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { IMAGE_URL } from "../../../constants";
import { MoveLeft, MoveRight } from "lucide-react";
import { Link } from "react-router-dom";

export default function JournalCarousel2({ data }) {
  const LOOP_OFFSET = data.length;

  const containerRef = useRef(null);
  const itemsRef = useRef([]);
  const [activeSlide, setActiveSlide] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(3);
  const offsetX = useMotionValue(0);
  const animatedX = useSpring(offsetX, { damping: 20, stiffness: 150 });

  // 🔁 Duplicate list for looping
  const loopedData = [...data, ...data];

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) setItemsPerView(1);
      else if (window.innerWidth < 768) setItemsPerView(2);
      else setItemsPerView(3);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getWidth = () => `${100 / itemsPerView}%`;

  function scrollNext() {
    const itemWidth = itemsRef.current[0]?.offsetWidth;
    const nextSlide = activeSlide + 1;
    setActiveSlide(nextSlide);
    offsetX.set(-nextSlide * itemWidth);

    // 🔄 Reset when reaching end of first set
    if (nextSlide >= LOOP_OFFSET) {
      setTimeout(() => {
        setActiveSlide(0);
        offsetX.set(0);
      }, 300); // let animation finish
    }
  }

  function scrollPrev() {
    const itemWidth = itemsRef.current[0]?.offsetWidth;
    const prevSlide = activeSlide - 1;
    setActiveSlide(prevSlide);
    offsetX.set(-prevSlide * itemWidth);

    // 🔄 Reset when going before start
    if (prevSlide < 0) {
      setTimeout(() => {
        setActiveSlide(LOOP_OFFSET - 1);
        offsetX.set(-(LOOP_OFFSET - 1) * itemWidth);
      }, 300);
    }
  }

  return (
    <div className="container px-4 mx-auto overflow-hidden md:px-6 lg:px-20">
      <div className="text-center">
        <h1 className="mb-4 text-4xl font-bold text-center font-domine">
          Journals
        </h1>
      </div>
      <div className="container mx-6 group">
        <div className="relative overflow-hidden">
          <motion.ul
            ref={containerRef}
            className="flex items-start"
            style={{ x: animatedX }}
            drag="x"
            dragElastic={0.1}
            onDragEnd={(_, info) => {
              const dragThreshold = 100;
              if (info.offset.x > dragThreshold) scrollPrev();
              else if (info.offset.x < -dragThreshold) scrollNext();
            }}
          >
            {loopedData.map((article, index) => (
              <motion.li
                key={index}
                ref={(el) => (itemsRef.current[index] = el)}
                className="group relative shrink-0 select-none md:px-3 transition-opacity duration-300"
                style={{ flexBasis: getWidth() }}
              >
                <div className="grid place-content-center overflow-hidden rounded-lg h-[400px] lg:max-h-[500px]">
                  <img
                    className="object-cover w-full h-[350px] rounded-lg"
                    src={`${IMAGE_URL}/${article?.image}`}
                    alt={article.title}
                  />
                </div>
                <div className="mt-4 flex justify-center">
                  <Link
                    to={article?.link}
                    className="text-xl font-bold leading-tight cursor-pointer hover:text-primary text-black"
                  >
                    {article.title}
                  </Link>
                </div>
              </motion.li>
            ))}
          </motion.ul>

          {/* controls */}
          <button
            type="button"
            className="absolute z-20 left-10 top-1/2"
            onClick={scrollPrev}
          >
            <MoveLeft className="p-3 text-white rounded-full size-12 bg-black/70" />
          </button>
          <button
            type="button"
            className="absolute z-20 right-10 top-1/2"
            onClick={scrollNext}
          >
            <MoveRight className="p-3 text-white rounded-full size-12 bg-black/70" />
          </button>
        </div>
      </div>
    </div>
  );
}
